import { ArrowRight } from "@phosphor-icons/react"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import Image from "next/image"
import Link from "next/link"
import type { HTMLAttributes } from "react"
import { twMerge } from "tailwind-merge"
import MarketingHeading from "../../common/MarketingHeading"

export type CaseStudy = {
  id: string
  tagline: string
  href: string
  backgroundImage: string
  logo: string
}

type Props = HTMLAttributes<HTMLDivElement> & {
  caseStudy: CaseStudy
}

const blurredBackgroundCss = twMerge(
  "blur-lg opacity-0 absolute inset-0 transition-opacity duration-300 pointer-events-none",
  "[&.grayscale]:opacity-[0.125] [&.grayscale]:blur-lg [&.grayscale]:grayscale(100%) [&:hover.grayscale]:opacity-0",
  "[.group:hover_&.grayscale]:opacity-0",
  "[.group:hover_&]:opacity-100",
)

const CaseStudyCard = ({ caseStudy, ...props }: Props) => {
  return (
    <Card
      as={caseStudy.href === "#" ? "div" : Link}
      // @ts-expect-error
      href={caseStudy.href}
      key={caseStudy.id}
      className="group w-[calc(100vw-100px)] lg:w-auto rounded-2xl p-6 flex-none lg:flex-[1_0_0] h-[390px] relative overflow-hidden text-decoration-none text-fg-default"
      {...props}
      style={{
        pointerEvents: caseStudy.href === "#" ? "none" : "auto",
        opacity: caseStudy.href === "#" ? 0.75 : 1,
      }}
    >
      <Image
        alt={caseStudy.id}
        src={caseStudy.backgroundImage}
        className={`${blurredBackgroundCss} grayscale`}
        height={325}
        width={658}
      />
      <Image
        alt={caseStudy.id}
        src={caseStudy.backgroundImage}
        className={blurredBackgroundCss}
        height={325}
        width={658}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-bg-default pointer-events-none" />
      <div
        className="flex flex-col items-center w-full justify-between h-full relative"
        style={{ zIndex: 1 }}
      >
        <div className="flex items-center justify-between w-full gap-2">
          <Image
            alt={caseStudy.id}
            className="opacity-85 transition-opacity duration-300 [&.group:hover_&]:opacity-100 object-contain object-left"
            src={caseStudy.logo}
            height={30}
            width={200}
          />
          <div>
            {caseStudy.href === "#" ? (
              <div
                className={twMerge(
                  "text-fg-secondary text-sm",
                  "border border-fg-border rounded-md font-medium text-sm mb-1.5 px-2.5 py-1.5 text-fg-secondary",
                )}
              >
                Coming soon
              </div>
            ) : (
              <Icon
                as={ArrowRight}
                width={20}
                className="text-fg-muted opacity-75 transition-opacity duration-300 [&.group:hover_&]:opacity-100"
              />
            )}
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <MarketingHeading
            level={2}
            className="mb-3"
            style={{
              lineHeight: "1.2",
            }}
          >
            {caseStudy.tagline}
          </MarketingHeading>
          <div className="text-fg-secondary text-sm">Learn more</div>
        </div>
      </div>
    </Card>
  )
}

export default CaseStudyCard
